import { useCallback, useState } from 'react';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DuplicateExternalRoiError } from '~/domain/errors/DomainError';
import { dcmjsSetString } from '~/domain/services/dicomProcessor';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import type { RoiId } from '~/domain/changes/ids';

export type ImageSeriesEdits = {
    seriesDescription?: string;
    seriesNumber?: number;
};

export type StructureSetEdits = {
    label?: string;
    approvalStatus?: DICOMApprovalStatus;
    reviewerName?: string;
};

export type RoiFieldEdits = {
    name?: string;
    interpretedType?: RoiInterpretedType;
};

/** Pending ROI edits by structure set UID, then by structure. */
export type RoiEdits = Record<string, Partial<Record<RoiId, RoiFieldEdits>>>;

type UseExportEditingResult = {
    imageEdits: Record<string, ImageSeriesEdits>;
    ssEdits: Record<string, StructureSetEdits>;
    roiEdits: RoiEdits;
    setImageSeriesDescription: (uid: string, description: string) => void;
    setImageSeriesNumber: (uid: string, seriesNumber: number) => void;
    setSsLabel: (uid: string, label: string) => void;
    setSsApproval: (uid: string, status: DICOMApprovalStatus) => void;
    setSsReviewer: (uid: string, name: string) => void;
    setRoiName: (ssUid: string, roiId: RoiId, name: string) => void;
    setRoiType: (ssUid: string, roiId: RoiId, type: RoiInterpretedType) => void;
    commitAllEdits: () => void;
};

const useExportEditing = (
    exportableImages: Image3D[],
    exportableStructureSets: StructureSet[]
): UseExportEditingResult => {
    const [imageEdits, setImageEdits] = useState<
        Record<string, ImageSeriesEdits>
    >({});
    const [ssEdits, setSsEdits] = useState<Record<string, StructureSetEdits>>(
        {}
    );
    const [roiEdits, setRoiEdits] = useState<RoiEdits>({});

    const session = useSessionOrNull();

    const setImageSeriesDescription = (uid: string, description: string) =>
        setImageEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], seriesDescription: description },
        }));

    const setImageSeriesNumber = (uid: string, seriesNumber: number) =>
        setImageEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], seriesNumber },
        }));

    const setSsLabel = (uid: string, label: string) =>
        setSsEdits((prev) => ({ ...prev, [uid]: { ...prev[uid], label } }));

    const setSsApproval = (uid: string, status: DICOMApprovalStatus) =>
        setSsEdits((prev) => ({
            ...prev,
            [uid]: {
                ...prev[uid],
                approvalStatus: status,
                // Clear reviewer when switching back to UNAPPROVED
                reviewerName:
                    status === DICOMApprovalStatus.UNAPPROVED
                        ? undefined
                        : prev[uid]?.reviewerName,
            },
        }));

    const setSsReviewer = (uid: string, name: string) =>
        setSsEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], reviewerName: name },
        }));

    const setRoiName = (ssUid: string, roiId: RoiId, name: string) =>
        setRoiEdits((prev) => ({
            ...prev,
            [ssUid]: {
                ...prev[ssUid],
                [roiId]: { ...prev[ssUid]?.[roiId], name },
            },
        }));

    const setRoiType = (
        ssUid: string,
        roiId: RoiId,
        type: RoiInterpretedType
    ) =>
        setRoiEdits((prev) => ({
            ...prev,
            [ssUid]: {
                ...prev[ssUid],
                [roiId]: {
                    ...prev[ssUid]?.[roiId],
                    interpretedType: type,
                },
            },
        }));

    const commitAllEdits = useCallback(() => {
        // Image edits mutate DICOM datasets directly — no tracker involvement.
        for (const image of exportableImages) {
            const edits = imageEdits[image.dicomUid];
            if (!edits) {
                continue;
            }
            for (const slice of image.imageSlices) {
                if (edits.seriesDescription !== undefined) {
                    dcmjsSetString(
                        slice.datasetWrapper,
                        'SeriesDescription',
                        edits.seriesDescription
                    );
                }
                if (edits.seriesNumber !== undefined) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (slice.datasetWrapper.dataset as any).SeriesNumber =
                        edits.seriesNumber;
                }
            }
        }

        // SS / ROI edits flow through the tracker so the RecoilBridge stays
        // in sync. If there is no active session (e.g. no patient loaded yet),
        // skip the domain mutations — there is nothing to commit.
        if (!session) {
            console.warn('Export commit attempted without active session');
            return;
        }

        session.context.tracker.batch(() => {
            for (const ss of exportableStructureSets) {
                const edits = ssEdits[ss.dicomUid];
                const rois = roiEdits[ss.dicomUid];

                if (!edits && !rois) {
                    continue;
                }

                // Step 1: SS label (must precede approval; ss.rename throws if
                // already approved).
                if (edits?.label !== undefined) {
                    ss.rename(edits.label);
                }

                // Step 2: ROI edits (must precede SS approval — once the SS is
                // APPROVED, DicomRoi._ensureCanModify() blocks ROI name
                // modifications).
                if (rois) {
                    for (const liveRoi of ss.rois) {
                        const fieldEdits = rois[liveRoi.id];
                        if (!fieldEdits) {
                            continue;
                        }

                        // Single-EXTERNAL backstop. This commit writes the
                        // entity setter directly (Roi.setInterpretedType is
                        // unguarded by design), so it bypasses the
                        // ensureExternalUnique check that RoiService performs
                        // on every other retype path. Validate through the
                        // domain guard — one authority for the rule — and
                        // refuse just this field, the way an ROI that no
                        // longer exists is skipped above. Validation runs
                        // BEFORE any write, so a refused type edit cannot
                        // leave the ROI unapproved for a change that never
                        // landed. The pending edit stays in `roiEdits`; the
                        // export UI does not offer EXTERNAL today, so this is
                        // a backstop against a non-service write path rather
                        // than a reachable workflow.
                        let interpretedTypeEdit = fieldEdits.interpretedType;
                        if (interpretedTypeEdit !== undefined) {
                            try {
                                ss.ensureExternalUnique(
                                    interpretedTypeEdit,
                                    liveRoi.id
                                );
                            } catch (e) {
                                if (!(e instanceof DuplicateExternalRoiError)) {
                                    throw e;
                                }
                                // Count-only, name-free: export messaging must
                                // stay PHI-free.
                                enqueueSnackbar(
                                    localization.t(
                                        'export.structureType.duplicateExternal'
                                    ),
                                    { variant: 'warning' }
                                );
                                interpretedTypeEdit = undefined;
                            }
                        }

                        // Name and interpreted type are both content edits
                        // that the domain blocks on an approved ROI. Clear
                        // ROI-level approval first if either is being edited:
                        // a reviewed ROI whose name or type changes before
                        // export can no longer be considered reviewed.
                        // (Previously only the name branch unapproved, so a
                        // type edit on an approved ROI threw, rolling back
                        // every queued export edit.)
                        const editsRoiContent =
                            fieldEdits.name !== undefined ||
                            interpretedTypeEdit !== undefined;
                        if (
                            editsRoiContent &&
                            liveRoi.approvalStatus !==
                                DICOMApprovalStatus.UNAPPROVED
                        ) {
                            liveRoi.unapprove();
                        }
                        if (fieldEdits.name !== undefined) {
                            liveRoi.rename(fieldEdits.name);
                        }
                        if (interpretedTypeEdit !== undefined) {
                            liveRoi.setInterpretedType(interpretedTypeEdit);
                        }
                    }
                }

                // Step 3: SS approval status.
                //
                // Branch A — Reviewer-only update: status not changed, only
                // reviewer name changes, SS is already APPROVED → update
                // reviewer without cascading to ROIs.
                if (
                    edits?.approvalStatus === undefined &&
                    edits?.reviewerName !== undefined &&
                    ss.approvalStatus === DICOMApprovalStatus.APPROVED
                ) {
                    const reviewerName = edits.reviewerName;
                    // setApprovalStatus pre-dates the tracker and does not
                    // call markChanged internally; we must do it explicitly.
                    ss.setApprovalStatus(
                        DICOMApprovalStatus.APPROVED,
                        reviewerName,
                        false
                    );
                    session.context.tracker.markChanged(ss);
                }

                // Branch B — Status change: update SS status, then cascade to
                // all ROIs except renamed ones (renaming after review means the
                // structure is no longer considered reviewed).
                if (edits?.approvalStatus !== undefined) {
                    const approvalStatus = edits.approvalStatus;
                    const reviewerName = edits.reviewerName ?? '';
                    const renamedRoiIds = new Set<RoiId>(
                        ss.rois
                            .filter((roi) => rois?.[roi.id]?.name !== undefined)
                            .map((roi) => roi.id)
                    );

                    // Use setApprovalStatus to ensure ReviewerName, ReviewDate,
                    // and ReviewTime are updated atomically in the DICOM dataset.
                    // cascade=false: we iterate ROIs manually below to exclude
                    // renamed ones.
                    ss.setApprovalStatus(approvalStatus, reviewerName, false);
                    session.context.tracker.markChanged(ss);

                    // Cascade to ROIs except renamed ones.
                    for (const liveRoi of ss.rois) {
                        if (renamedRoiIds.has(liveRoi.id)) {
                            continue;
                        }
                        if (approvalStatus === DICOMApprovalStatus.APPROVED) {
                            liveRoi.approve(reviewerName);
                        } else if (
                            approvalStatus === DICOMApprovalStatus.REJECTED
                        ) {
                            liveRoi.reject(reviewerName);
                        } else {
                            liveRoi.unapprove();
                        }
                    }
                }
            }
        });
    }, [
        exportableImages,
        exportableStructureSets,
        imageEdits,
        ssEdits,
        roiEdits,
        session,
    ]);

    return {
        imageEdits,
        ssEdits,
        roiEdits,
        setImageSeriesDescription,
        setImageSeriesNumber,
        setSsLabel,
        setSsApproval,
        setSsReviewer,
        setRoiName,
        setRoiType,
        commitAllEdits,
    };
};

export default useExportEditing;
